import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ActionGroupCandidate } from "../../types/candidate";
import {
  readSelectionParams,
  resolveSelection,
  selectionParamsFor,
  withSelectionParams,
} from "./selection-url";
import type { CurrSection } from "./types";

const TRIGGER: CurrSection = { type: "trigger", hash: "" };

interface SelectionUrlSyncOptions {
  automationId: string | undefined;
  historyId: string | undefined;
  /** The current route is Run History (`:id/history[/:historyId]`). */
  routeIsHistory: boolean;
  isNewAutomation: boolean;
  /** The candidate holds the automation: the saved one cloned in, or a new
   * automation's draft. The clone regenerates every hash, so the URL is
   * neither read nor written before. */
  isHydrated: boolean;
  /** Read when the effect runs: the clone happens after the render. */
  getGroups: () => ActionGroupCandidate[];
  activeItem: CurrSection;
  prevActiveItem: CurrSection;
  setActiveItem: (section: CurrSection) => void;
  setPrevActiveItem: (section: CurrSection) => void;
  onActionNotFound: () => void;
}

export const useSelectionUrlSync = ({
  automationId,
  historyId,
  routeIsHistory,
  isNewAutomation,
  isHydrated,
  getGroups,
  activeItem,
  prevActiveItem,
  setActiveItem,
  setPrevActiveItem,
  onActionNotFound,
}: SelectionUrlSyncOptions): void => {
  const navigate = useNavigate();
  const location = useLocation();
  // The URL the selection was last reconciled with. When the URL differs
  // from it, the URL changed (deep link, Back, Copilot link) and wins;
  // otherwise the selection changed and is written.
  const syncedUrl = useRef<string>();

  const base = isNewAutomation
    ? "/create"
    : automationId === undefined
      ? undefined
      : `/${automationId}`;
  const enabled = isHydrated && base !== undefined;
  const inHistory = activeItem?.type === "history";
  // Run History keeps highlighting the section it was opened from and
  // returns to it on Go back, so that section is the one its URL names.
  const selection = inHistory ? prevActiveItem : activeItem;

  useEffect(() => {
    if (!enabled || base === undefined) return;
    const { pathname, search } = location;

    if (syncedUrl.current !== pathname + search) {
      syncedUrl.current = pathname + search;
      const wanted = readSelectionParams(new URLSearchParams(search));
      const current = selectionParamsFor(selection, getGroups());
      let target = selection;
      if (
        current.action !== wanted.action ||
        current.branch !== wanted.branch
      ) {
        const section = resolveSelection(wanted, getGroups());
        // A reloaded new automation lost its draft: nothing to report.
        if (section === undefined && wanted.action !== undefined) {
          if (!isNewAutomation) onActionNotFound();
        }
        target = section ?? (isNewAutomation ? null : TRIGGER);
      }
      // The route, not the previous selection, decides between Run History
      // and the editor: a link or Back can change both at once.
      if (routeIsHistory) {
        const opensHistory = !inHistory;
        if (target !== prevActiveItem) setPrevActiveItem(target);
        if (opensHistory) setActiveItem({ type: "history", hash: "" });
        if (target !== prevActiveItem || opensHistory) return;
      } else if (target !== activeItem) {
        setActiveItem(target);
        return;
      }
      // The URL named nothing the selection can become (e.g. an action that is
      // gone): fall through and write the selection back instead.
    }

    // A new automation has no Run History route: it stays on /create.
    const nextPathname =
      !inHistory || isNewAutomation
        ? base
        : historyId === undefined
          ? `${base}/history`
          : `${base}/history/${historyId}`;
    const query = withSelectionParams(
      new URLSearchParams(search),
      selectionParamsFor(selection, getGroups()),
    ).toString();
    const nextSearch = query === "" ? "" : `?${query}`;
    if (nextPathname === pathname && nextSearch === search) return;

    syncedUrl.current = nextPathname + nextSearch;
    // Only the query changed: replace, so Back leaves the automation instead
    // of replaying clicks. A new path (Run History, back to the editor) pushes.
    navigate(
      { pathname: nextPathname, search: nextSearch },
      { replace: nextPathname === pathname },
    );
  }, [enabled, location.pathname, location.search, activeItem, prevActiveItem]);
};
